import { useEffect, useMemo, useState } from 'react';
import { Grid, ThemeProvider, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import composeTheme from '@theme/theme';
import { useStore } from '@store/GlobalStore';
import {
  CustomerPostRequestBody,
  CustomerPostResponse,
  QuotePostRequestBody,
  QuotePostResponse,
  QuoteSetupPresetGetResponse,
} from '@lio/api-types';
import { useNewQuoteStore } from '@store/NewQuote';
import { useSearchQuoteSetupPreset } from '@services/quotes/quoteSetupPreset';
import { useCreateCustomer, useCreateInsuredLocation, useUpdateCustomerAddressesContactInfo } from '@services/customer';
import { ToastType } from '@components/toast/ToastType.enum';
import { initialState, IPanel, initialNameInsuredState, PanelType, ERROR } from '@pages/NewQuote/NewQuote.constants';
import { AddressResponse, AddressType, BusinessType, Customer, InsuredLocationStatus } from '@models/customer';
import { Button } from '@components/button';
import { setDateToMidnight } from '@utils/date';
import { Layout } from '@layout/Layout';
import { useLayoutStore } from '@store/LayoutStore';
import { TogglePanel } from './TogglePanel';
import { LocationClosedPanel, LocationOpenPanel } from './location';
import { NameInsuredClosedPanel, NamedInsuredOpenPanel } from './namedInsured';
import { ScopeClosedPanel, ScopeOpenPanel } from './scope';
import { QuotePresetErrorPanel } from './QuotePresetErrorPanel';
import { addDays } from 'date-fns';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { useCreateQuoteWithNonLiveJurisdictionCheck } from './useCreateQuoteWithNonLiveJurisdictionCheck';
import { NonLiveJurisdictionDialog } from './NonLiveJurisdictionDialog';
import { NonLiveJurisdictionDialogCancelledError } from './nonLiveJurisdiction';

type CreatedCustomer = Extract<CustomerPostResponse, { id: string }>;

// The matcher can find a conflicting existing customer at creation time, after the panel's own
// pre-check already returned no-match — a low-probability race. Distinguishing this from other
// customer-creation failures lets the caller send the user back to resolve it instead of hitting
// a dead end behind a generic error.
class CustomerMatchConflictError extends Error {}

const ScopePanel = TogglePanel(ScopeOpenPanel, ScopeClosedPanel);
const NameInsuredPanel = TogglePanel(NamedInsuredOpenPanel, NameInsuredClosedPanel);
const LocationPanel = TogglePanel(LocationOpenPanel, LocationClosedPanel);

export const NewQuotePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const quotePresetSlug = searchParams.get('preset');
  const asRenewalParam = searchParams.get('asRenewal');

  const { closeHeaderMenu } = useLayoutStore(
    (state) => ({
      closeHeaderMenu: state.closeHeaderMenu,
    }),
    shallow,
  );

  const [panel, setPanel] = useState(initialState);
  const { isCustomerUser, isInternalUser } = useStore((state) => state.userAuth, shallow);
  const [isCreatingQuoteLoading, setIsCreatingQuoteLoading] = useState(false);
  const isRenewalMode = useMemo(() => asRenewalParam === 'true' && !!isInternalUser, [asRenewalParam, isInternalUser]);
  const [existingPolicyNumber, setExistingPolicyNumber] = useState('');
  const [renewalTermToBind, setRenewalTermToBind] = useState('');
  const isExistingPolicyValid = !isRenewalMode || (/^\d+$/.test(existingPolicyNumber) && existingPolicyNumber.length);
  const renewalTermInt = parseInt(renewalTermToBind, 10);
  const isRenewalTermValid = !isRenewalMode || (Number.isInteger(renewalTermInt) && renewalTermInt > 0);
  const basePanelsComplete = Object.values(panel).every((p) => !p.isOpen && p.isCompleted);
  const canCreateQuote = basePanelsComplete && isExistingPolicyValid && isRenewalTermValid;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { resetStorage, scope, namedInsured, location, updateNamedInsurance, updateAddress } = useNewQuoteStore();
  const { createCustomerAsync } = useCreateCustomer();
  const { createInsuredLocationAsync } = useCreateInsuredLocation();
  const {
    createQuote,
    isNonLiveJurisdictionDialogOpen,
    isResubmitting: isResubmittingNonLiveJurisdictionChoice,
    onCreateAndDecline: handleCreateAndDeclineNonLiveJurisdiction,
    onCreateInternalOnly: handleCreateInternalOnlyNonLiveJurisdiction,
    onCancelNonLiveJurisdictionDialog,
  } = useCreateQuoteWithNonLiveJurisdictionCheck(!!isInternalUser);

  const { updateCustomerContactInfo } = useUpdateCustomerAddressesContactInfo();
  const { isMobileOrSmallScreen } = useGetScreenSize();

  const { quotePresets, isLoading } = useSearchQuoteSetupPreset(
    { slug: quotePresetSlug ?? undefined },
    !!quotePresetSlug,
  );
  const quotePreset = quotePresets?.[0]; // will only be one if filtering by slug

  if (isCustomerUser && !quotePresetSlug) {
    navigate('/');
  }

  useEffect(() => () => closeSnackbar(), [closeSnackbar]);

  useEffect(
    () => () => {
      resetStorage();
    },
    [resetStorage],
  );

  useEffect(() => {
    closeHeaderMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isSpecialEvent } = scope;

  // Remove asRenewal if user not internal
  useEffect(() => {
    if (asRenewalParam === 'true' && !isInternalUser) {
      const params = new URLSearchParams(searchParams);
      params.delete('asRenewal');
      setSearchParams(params, { replace: true });
    }
  }, [asRenewalParam, isInternalUser, searchParams, setSearchParams]);

  const handleClosePanelChange = (panelToClose: PanelType, panelToOpen?: PanelType) => {
    if (panelToOpen) {
      setPanel((prevState) => ({
        ...prevState,
        [panelToOpen]: {
          show: true,
          isOpen: true,
          isCompleted: panel[panelToOpen].isCompleted,
        },
        [panelToClose]: {
          show: true,
          isOpen: false,
          isCompleted: true,
        },
      }));
    } else {
      setPanel((prevState) => ({
        ...prevState,
        [panelToClose]: {
          show: true,
          isOpen: false,
          isCompleted: true,
        },
      }));
    }
  };

  const handleOpenPanelChange = (panelToOpen: PanelType) => {
    const closedPanels: IPanel = Object.entries(panel).reduce(
      (acc, [k, v]) =>
        Object.assign(acc, {
          [k]: {
            ...v,
            show: v.isCompleted,
            isOpen: false,
          },
        }),
      {} as IPanel,
    );
    setPanel(() => ({
      ...closedPanels,
      [panelToOpen]: {
        show: true,
        isOpen: true,
        isCompleted: panel[panelToOpen].isCompleted,
      },
    }));
  };

  const handleFieldChange = (newState: IPanel) => {
    setPanel(newState);
  };

  const getQuotePostRequestBody = (insuredLocationIds: string[], customerId: string) => {
    const {
      brokerageAgency,
      brokerageProducer,
      productOffering,
      productLabel,
      productOption,
      effectiveDate,
      expirationDate,
      writingCompany,
    } = scope;
    if (!brokerageAgency) {
      console.warn('Missing Brokerage Agency');
      return undefined;
    }
    if (!brokerageProducer) {
      console.warn('Missing Brokerage Producer');
      return undefined;
    }
    if (!productOffering) {
      console.warn('Missing Product Offering');
      return undefined;
    }
    if (!isSpecialEvent && !effectiveDate) {
      console.warn('Missing Effective Date');
      return undefined;
    }
    if ((isSpecialEvent && !effectiveDate) || (isSpecialEvent && !expirationDate)) {
      console.warn('Missing Event Date Range');
      return undefined;
    }

    const body = {
      brokerageId: brokerageAgency.value,
      brokerageProducerId: brokerageProducer.value,
      offeringId: productOffering.value,
      productOptionId: quotePreset?.productOption ? quotePreset.productOption.id : productOption?.id,
      productLabelId: productLabel?.id,
      insuredLocationIds,
      effectiveOn: effectiveDate ? setDateToMidnight(effectiveDate) : null,
      expiresOn: expirationDate ? setDateToMidnight(addDays(expirationDate, 1)) : undefined,
      customerId,
      writingCompanyId: writingCompany?.id,
      createdWithQuoteSetupPresetId: quotePreset ? quotePreset.id : undefined,
      formsV2Enabled: true,
    } as QuotePostRequestBody;
    if (isRenewalMode && isExistingPolicyValid && isRenewalTermValid) {
      body.policyNumber = { policyNumber: existingPolicyNumber };
      body.bindWithRenewalTerm = renewalTermInt;
    }
    return body;
  };

  const handleCreateCustomer = async (): Promise<CreatedCustomer | Customer | undefined> => {
    const { customer } = namedInsured;
    if (!customer) return undefined;
    const { name, businessType, businessAddress, addresses, createAsEntered } = customer;
    const customerExists = !!customer.id;
    if (customerExists) {
      if (!addresses?.find((address: AddressResponse) => address.type === AddressType.Business)) {
        const newAddress = {
          ...(businessAddress as AddressResponse),
          type: AddressType.Business as AddressResponse['type'],
        };
        await updateCustomerContactInfo({
          customerId: customer.id,
          addresses: addresses
            ? [
                ...addresses.map((a: AddressResponse) => ({
                  id: a.id,
                  careOf: a.careOf,
                  street1: a.street1,
                  street2: a.street2,
                  city: a.city,
                  state: a.state,
                  zipCode: a.zipCode,
                  type: a.type,
                })),
                newAddress,
              ]
            : [newAddress],
        });
      }
      return Promise.resolve(customer);
    }

    // If customer's id is missing, they did not select an existing customer from the matcher's
    // candidates, so create a new customer using what was entered.
    const customerPostRequestBody: CustomerPostRequestBody = {
      name,
      businessType: businessType as BusinessType,
      businessAddress: businessAddress as CustomerPostRequestBody['businessAddress'],
      createAsEntered: createAsEntered || undefined,
    };
    return createCustomerAsync(customerPostRequestBody).then((data) => {
      if (data.outcome === 'propose') {
        // The matcher found a conflicting candidate at creation time; surface this as an error
        // rather than silently creating a mismatched record.
        throw new CustomerMatchConflictError('A matching customer was found while creating this customer.');
      }

      const {
        id,
        name: createdName,
        createdAt,
        updatedAt,
        state,
        city,
        businessType: createdBusinessType,
        deletedAt,
        jurisdiction,
        deletedByUserId,
      } = data;
      updateNamedInsurance({
        id,
        name: createdName.trim(),
        deletedAt,
        createdAt,
        updatedAt,
        jurisdiction,
        deletedByUserId,
        state: state || '',
        city: city || '',
        businessType: createdBusinessType as BusinessType,
      });
      return data;
    });
  };

  const handleCreateInsuredLocations = async (customerId: string): Promise<string> => {
    const { insuredLocation } = location;

    if (!insuredLocation.id) {
      return Promise.resolve(
        createInsuredLocationAsync({
          status: InsuredLocationStatus.Approved,
          label: '',
          customerId,
          isPrimary: true,
          address: {
            street1: insuredLocation.street1,
            street2: insuredLocation.street2 || undefined,
            state: insuredLocation.state,
            city: insuredLocation.city,
            zipCode: insuredLocation.zipCode,
          },
        }),
      ).then((res) => {
        updateAddress({ ...insuredLocation, id: res.id });
        return res.id;
      });
    }
    return insuredLocation.id;
  };

  const handleCreateQuote = (quoteBody: QuotePostRequestBody): Promise<QuotePostResponse> => createQuote(quoteBody);

  const handleError = (message: string) => {
    enqueueSnackbar(message, {
      persist: true,
      variant: ToastType.Error,
    });
    setIsCreatingQuoteLoading(false);
  };

  const handleContinueToSubmission = async () => {
    setIsCreatingQuoteLoading(true);
    handleCreateCustomer()
      .then((customerResponse) => {
        const customerId = customerResponse?.id as string;
        handleCreateInsuredLocations(customerId)
          .then((insuredLocationIds) => {
            const quoteBody = getQuotePostRequestBody([insuredLocationIds], customerId);
            if (!quoteBody) {
              handleError(ERROR.CREATE_QUOTE);
              return;
            }
            handleCreateQuote(quoteBody)
              .then((quoteResponse) => {
                const { id } = quoteResponse;
                navigate(`/quote/${id}`);
              })
              .catch((e) => {
                if (e instanceof NonLiveJurisdictionDialogCancelledError) {
                  // The user closed the jurisdiction-not-live dialog without choosing an option;
                  // just reset the form back to its normal state without an error toast.
                  setIsCreatingQuoteLoading(false);
                  return;
                }
                console.warn(e);
                handleError(ERROR.CREATE_QUOTE);
              });
          })
          .catch((e) => {
            console.warn(e);
            handleError(ERROR.CREATE_INSURED_LOCATIONS);
          });
      })
      .catch((e) => {
        console.warn(e);
        if (e instanceof CustomerMatchConflictError) {
          handleError(ERROR.CUSTOMER_MATCH_CONFLICT);
          handleOpenPanelChange(PanelType.NameInsured);
          return;
        }
        handleError(ERROR.CREATE_CUSTOMER);
      });
  };

  return (
    <ThemeProvider theme={composeTheme('dark')}>
      <Layout title={isRenewalMode ? 'New Renewal Setup' : 'New Quote Setup'}>
        {isCustomerUser && !quotePreset && !isLoading ? (
          <QuotePresetErrorPanel />
        ) : (
          <Grid
            container
            sx={{
              justifyContent: 'center',
              paddingBottom: '10px',
              paddingTop: isMobileOrSmallScreen ? '' : '50px',
            }}
          >
            <Grid
              size={{ xs: 12, sm: 8 }}
              container
              rowSpacing='10px'
              sx={{
                margin: isMobileOrSmallScreen ? 1 : 0,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {panel?.scope?.show && (
                <Grid size={{ xs: 12 }}>
                  <ScopePanel
                    isOpen={panel.scope.isOpen}
                    onOpen={() => handleOpenPanelChange(PanelType.Scope)}
                    onClose={() => handleClosePanelChange(PanelType.Scope, PanelType.NameInsured)}
                    onFieldChange={(value) => {
                      if (value) {
                        handleFieldChange(initialState);
                      }
                    }}
                    quotePreset={quotePreset as QuoteSetupPresetGetResponse}
                    isRenewalMode={isRenewalMode}
                    existingPolicyNumber={existingPolicyNumber}
                    setExistingPolicyNumber={setExistingPolicyNumber}
                    renewalTermToBind={renewalTermToBind}
                    setRenewalTermToBind={setRenewalTermToBind}
                  />
                </Grid>
              )}
              {panel?.nameInsured?.show && (
                <Grid size={{ xs: 12 }} data-testid='NamedInsured'>
                  <NameInsuredPanel
                    isOpen={panel.nameInsured.isOpen}
                    onOpen={() => handleOpenPanelChange(PanelType.NameInsured)}
                    onClose={() => handleClosePanelChange(PanelType.NameInsured, PanelType.Location)}
                    onFieldChange={(value) => {
                      if (value) {
                        handleFieldChange(initialNameInsuredState);
                      }
                    }}
                  />
                </Grid>
              )}
              {panel?.location?.show && (
                <Grid size={{ xs: 12 }} data-testid='Location'>
                  <LocationPanel
                    isOpen={panel.location.isOpen}
                    hasIneligibilities={false}
                    onOpen={() => handleOpenPanelChange(PanelType.Location)}
                    onClose={() => handleClosePanelChange(PanelType.Location)}
                  />
                </Grid>
              )}
              {canCreateQuote && (
                <Grid
                  container
                  size={{ xs: 11 }}
                  sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant='bodySmall'
                    sx={{
                      fontWeight: 'bold',
                      display: 'inline',
                    }}
                  >
                    Great! Please confirm that the information you&#39;ve entered above is correct before proceeding.
                  </Typography>

                  <Grid
                    sx={{
                      marginTop: '30px',
                    }}
                  >
                    <Button variant='contained' isLoading={isCreatingQuoteLoading} onClick={handleContinueToSubmission}>
                      Continue to Submission Details
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </Layout>
      <NonLiveJurisdictionDialog
        isOpen={isNonLiveJurisdictionDialogOpen}
        isSubmitting={isResubmittingNonLiveJurisdictionChoice}
        onCancel={onCancelNonLiveJurisdictionDialog}
        onCreateAndDecline={handleCreateAndDeclineNonLiveJurisdiction}
        onCreateInternalOnly={handleCreateInternalOnlyNonLiveJurisdiction}
      />
    </ThemeProvider>
  );
};
